import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'

import ContentBlock from '@/components-latest/primitives/ContentBlock'
import MoreOptions, {
  MoreOptionsItem
} from '@/components-latest/primitives/MoreOptions'
import Separator from '@/components-latest/primitives/Separator'
import Spinner from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import DisableConfirmation from '@/components-latest/ui/OfferPage/DisableConfirmation'
import { DEAL_STATUS, DEAL_TYPE } from '@/constants/storeEvents'
import { addSystemNotification } from '@/ducks/notifications/slices/notification.slice'
import {
  useDeleteCartDiscountMutation,
  useUpdateCartDiscountMutation
} from '@/ducks/offers/api/offers.api'
import { selectCurrencyCode } from '@/ducks/store/selectors/store.selectors'
import { trackDealStatusChange } from '@/utils/dealEvents.utils'

import Styles from './CartDiscountItem.module.scss'

interface Discount {
  id: string
  percentage: number
  status: string
  minimumAmount: number
  maximumOff: number
  expirationDate: string
  discountType: string
}

const DISCOUNT_STATUS = {
  ACTIVE: 1,
  INACTIVE: 2
}

type Props = {
  isLoading?: boolean
  discount?: Discount
  onEdit?: (itemId: string) => void
  onDelete?: (itemId: string) => void
}

const CartDiscountItem = React.memo(
  ({ isLoading, discount, onEdit }: Props) => {
    const { t } = useTranslation()
    const currency = useSelector(selectCurrencyCode)
    const dispatch = useDispatch()
    const [deleteCartDiscount] = useDeleteCartDiscountMutation()
    const [updateCartDiscount] = useUpdateCartDiscountMutation()
    const deleteDiscount = async (discountId: string) => {
      try {
        const result = await deleteCartDiscount(discountId).unwrap()
        if (result?.status === '1') {
          dispatch(
            addSystemNotification({
              message: t('offers.cart-discount.more-actions.success'),
              intent: 'success'
            })
          )
        }
      } catch (error) {
        dispatch(
          addSystemNotification({
            message: t('offers.cart-discount.more-actions.success'),
            intent: 'error'
          })
        )
      }
    }
    const updateDiscount = async (discountId: string, status: string) => {
      try {
        const result = await updateCartDiscount({
          status:
            status === 'Active'
              ? DISCOUNT_STATUS.INACTIVE
              : DISCOUNT_STATUS.ACTIVE,
          cartId: discountId
        }).unwrap()
        if (result?.message === 'success') {
          trackDealStatusChange(
            discountId,
            DEAL_TYPE.CART_DISCOUNT,
            status === 'Active'
              ? DEAL_STATUS.DEACTIVATED
              : DEAL_STATUS.ACTIVATED
          )
          dispatch(
            addSystemNotification({
              message:
                status === 'Active'
                  ? t(
                      'offers.cart-discount.more-actions.deactivate-offer.success'
                    )
                  : t(
                      'offers.cart-discount.more-actions.activate-offer.success'
                    ),
              intent: 'success'
            })
          )
        }
      } catch (error) {
        dispatch(
          addSystemNotification({
            message: t('offers.cart-discount.more-actions.success'),
            intent: 'error'
          })
        )
      }
    }
    const [deleteModalIsOpen, setDeleteModalIsOpen] = useState(false)
    const [deactivateModalIsOpen, setDeactivateModalIsOpen] = useState(false)

    const DISCOUNTS_TYPE = {
      PERCENTAGE: 'Percentage',
      FLAT: 'Flat'
    }

    const handleDeactivateOffer = () => {
      if (!discount) return
      updateDiscount(discount.id, discount.status)
      setDeactivateModalIsOpen(false)
    }
    return (
      <ContentBlock className={Styles.wrapper}>
        {isLoading ? (
          <div className={Styles.spinner} data-testid="loading-spinner-cart">
            <Spinner />
          </div>
        ) : discount ? (
          <>
            <div className={Styles.header}>
              <div className={Styles.headerLeft}>
                <Text variant="title-1">
                  {discount.discountType === DISCOUNTS_TYPE.PERCENTAGE
                    ? `${discount.percentage}% off`
                    : `${discount.percentage} ${currency}`}
                </Text>
                <span className={Styles.activeStatus}>{discount.status}</span>
              </div>
              <MoreOptions
                popoverClassName={Styles.optionsContainer}
                align="start"
                noArrow>
                <MoreOptionsItem
                  id={discount.id}
                  onClick={() => onEdit?.(discount.id)}
                  className={Styles.option}>
                  <Text variant="nextSubtitle">{t('offers.edit-offer')}</Text>
                </MoreOptionsItem>
                <MoreOptionsItem
                  id={discount.id}
                  onClick={() => setDeactivateModalIsOpen(true)}
                  className={Styles.option}>
                  <Text variant="nextSubtitle">
                    {discount.status === 'Active'
                      ? t(
                          'offers.cart-discount.more-actions.deactivate-offer.title'
                        )
                      : t(
                          'offers.cart-discount.more-actions.activate-offer.title'
                        )}
                  </Text>
                </MoreOptionsItem>
                <MoreOptionsItem
                  id={discount.id}
                  onClick={() => setDeleteModalIsOpen(true)}
                  className={Styles.option}>
                  <Text variant="nextSubtitle">{t('offers.delete-offer')}</Text>
                </MoreOptionsItem>
              </MoreOptions>
            </div>

            <Separator variant="solid" />

            <div className={Styles.detailsSection}>
              <Text variant="title-3" className={Styles.detailsTitle}>
                {t('offers.cart-discount.details')}
              </Text>
              <div className={Styles.detailItem}>
                <Text variant="subtitle">
                  {t('offers.cart-discount.target-amount.title')}
                </Text>
                <Text variant="subtitle">
                  {discount.minimumAmount} {currency}
                </Text>
              </div>
              {discount.discountType === DISCOUNTS_TYPE.PERCENTAGE && (
                <>
                  <Separator variant="squared" className={Styles.separator} />
                  <div className={Styles.detailItem}>
                    <Text variant="subtitle">
                      {t('offers.cart-discount.max-off.title')}
                    </Text>
                    <Text variant="subtitle">
                      {discount.maximumOff} {currency}
                    </Text>
                  </div>
                </>
              )}
              <Separator variant="squared" className={Styles.separator} />
              <div className={Styles.detailItem}>
                <Text variant="subtitle">
                  {t('offers.cart-discount.end-date.title')}
                </Text>
                <Text variant="subtitle">{discount.expirationDate}</Text>
              </div>
            </div>

            <Separator variant="squared" className={Styles.separator} />

            <DisableConfirmation
              open={deleteModalIsOpen}
              onOpenChange={setDeleteModalIsOpen}
              onConfirm={() => deleteDiscount(discount.id)}
              title={t('offers.cart-discount.more-actions.delete-offer.title')}
              description={t(
                'offers.cart-discount.more-actions.delete-offer.description'
              )}
              cancelText={t('common.modal.cancel')}
              confirmText={t('common.modal.confirm')}
            />

            <DisableConfirmation
              open={deactivateModalIsOpen}
              onOpenChange={setDeactivateModalIsOpen}
              onConfirm={handleDeactivateOffer}
              title={
                discount.status === 'Active'
                  ? t(
                      'offers.cart-discount.more-actions.deactivate-offer.title'
                    )
                  : t('offers.cart-discount.more-actions.activate-offer.title')
              }
              description={
                discount.status === 'Active'
                  ? t(
                      'offers.cart-discount.more-actions.deactivate-offer.description'
                    )
                  : t(
                      'offers.cart-discount.more-actions.activate-offer.description'
                    )
              }
              cancelText={t('common.modal.cancel')}
              confirmText={t('common.modal.confirm')}
            />
          </>
        ) : (
          <Text variant="nextSubtitle">{t('offers.no-data.title')}</Text>
        )}
      </ContentBlock>
    )
  }
)

CartDiscountItem.displayName = 'CartDiscountItem'
export default CartDiscountItem
